// Account state of the ACS prototype (#/profil, #/parametres and the avatar menu of the home). FICTITIOUS demo values, kept in
// memory for the page session only: leaving and reopening a page shows the same values, a reload returns to these fixtures.
// Nothing is sent, uploaded or persisted; no browser permission is ever requested (the phone channel state is a demo setting).
import { useSyncExternalStore } from "react";
import { MEMBER } from "./home-fixtures";
import { PHOTOS } from "./photos";

export type PhoneChannel = "active" | "a-autoriser" | "bloque";
// "marketing" = use of the member's photos in the studio's communication (optional, off by default); "photos" = progress photos.
export type ConsentId = "suivi" | "sensibles" | "photos" | "marketing";
export type NotifId = "messages" | "programme" | "actualites" | "rappels";
export const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"] as const;

export interface Account {
  firstName: string; lastName: string; email: string; phone: string;
  /** Photo shown in the profile and the avatar menu: the validated avatar fixture, or a local preview (object URL, never uploaded). */
  photo: string; memberSince: string;
  birthDate: string; heightCm: number;
  notif: Record<NotifId, boolean>;
  reminders: { days: string[]; time: string };
  phoneChannel: PhoneChannel; emailChannel: boolean;
  consents: Record<ConsentId, boolean>;
  exportRequested: boolean; deletionRequested: boolean;
}

let state: Account = {
  // Demo identity: first name of the home fixture; last name, e-mail (reserved example domain) and phone are placeholders.
  firstName: MEMBER.firstName, lastName: "Martin", email: "camille@example.com", phone: "06 00 00 00 00",
  // Fictive member: no photo (the avatar shows her initials); the ACS photos are of real people and are not used for her.
  photo: "", memberSince: "septembre 2026",
  birthDate: "1990-05-14", heightCm: 168,
  notif: { messages: true, programme: true, actualites: true, rappels: true },
  reminders: { days: ["Lun", "Mer", "Ven"], time: "07:30" },
  phoneChannel: "a-autoriser", emailChannel: false,
  consents: { suivi: true, sensibles: false, photos: false, marketing: false },
  exportRequested: false, deletionRequested: false,
};
const listeners = new Set<() => void>();
export const account = {
  get: () => state,
  subscribe: (l: () => void) => { listeners.add(l); return () => listeners.delete(l); },
  set: (patch: Partial<Account>) => { state = { ...state, ...patch }; listeners.forEach(l => l()); },
  /** Local preview of a picked photo; the previous preview URL is released. */
  setPhoto: (file: File) => {
    if (state.photo.startsWith("blob:")) URL.revokeObjectURL(state.photo);
    account.set({ photo: URL.createObjectURL(file) });
  },
};
export const useAccount = () => useSyncExternalStore(account.subscribe, account.get, account.get);
export const fullName = (a: Account) => [a.firstName, a.lastName].filter(Boolean).join(" ");
